// ContactForm — the one interactive feature (plan §1, §7). Collects the contract
// fields, validates client-side with the shared `contactRequestSchema` BEFORE
// submitting, and maps the BFF's response envelope to bilingual UX.
//
// Anti-spam (plan §7.4) handled here on the client side:
//   * Honeypot field `website` — visually hidden, aria-hidden, not tab-focusable;
//     must stay empty (a bot filling it is rejected server-side).
//   * `renderedAt` — captured when the form mounts; sent for the server time-trap.
//   * Turnstile token — supplied by <Turnstile/> when a site key exists; omitted
//     (optional) when Turnstile is disabled in v1.
//
// Response mapping (plan §7.2 / contract error codes):
//   201            -> success state
//   400 VALIDATION -> field errors (from details) + generic message
//   403 CSRF       -> "refresh and try again"
//   403 ANTISPAM   -> "could not verify, try again"
//   429 DAILY_LIMIT-> "daily limit reached, email us / try tomorrow"
//   503 UNAVAILABLE / 500 INTERNAL -> "service unavailable"
//   network error  -> "check your connection"
// Raw error internals are never surfaced.
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  contactRequestSchema,
  type ContactRequest,
  type ContactResponse,
  type ContactErrorCode,
  type Locale,
} from "@shared/contact-contract";
import { submitContact } from "../api/client";
import Turnstile from "./Turnstile";

const MESSAGE_MAX = 4000;

// Shared field styling (Tailwind + brand tokens) used by the inline message field
// and the <Field/> subcomponent so inputs/labels/errors look identical.
const LABEL_CLASS =
  "mb-1.5 flex items-center gap-2 text-[var(--text-sm)] font-medium text-[var(--ink)]";
const REQ_CLASS = "text-[var(--text-xs)] font-normal text-[var(--brand-primary-text)]";
const OPT_CLASS = "text-[var(--text-xs)] font-normal text-[var(--brand-slate-text)]";
const ERROR_CLASS = "text-[var(--text-sm)] text-red-600";

/** Input/textarea classes; red ring + border when invalid. */
function inputClass(invalid: boolean): string {
  return [
    "w-full rounded-[var(--radius-md)] border bg-white px-3.5 py-2.5 text-[var(--text-base)] text-[var(--ink)]",
    "placeholder:text-[var(--brand-slate-text)] transition-colors",
    "focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1",
    invalid
      ? "border-red-400 focus-visible:border-red-500 focus-visible:ring-red-300"
      : "border-[var(--brand-tint-2)] focus-visible:border-[var(--brand-azure)] focus-visible:ring-[var(--brand-tint)]",
  ].join(" ");
}

type FieldName = "name" | "company" | "email" | "phone" | "subject" | "message";

interface FormValues {
  name: string;
  company: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
  /** Honeypot — must remain empty. */
  website: string;
}

const EMPTY: FormValues = {
  name: "",
  company: "",
  email: "",
  phone: "",
  subject: "",
  message: "",
  website: "",
};

type FieldErrors = Partial<Record<FieldName, string>>;

export interface ContactFormProps {
  /** Active locale; stamped onto the request and passed to Turnstile. */
  locale: Locale;
  /** Turnstile site key from /config.json (null => Turnstile disabled). */
  turnstileSiteKey: string | null;
}

export default function ContactForm({ locale, turnstileSiteKey }: ContactFormProps) {
  const { t } = useTranslation("contact");

  const [values, setValues] = useState<FormValues>(EMPTY);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [succeeded, setSucceeded] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

  // Time-trap baseline: ms epoch when the form mounted.
  const renderedAtRef = useRef<number>(Date.now());

  // Re-baseline the timestamp whenever we reset back to a fresh form.
  useEffect(() => {
    if (!succeeded) renderedAtRef.current = Date.now();
  }, [succeeded]);

  function setField(name: keyof FormValues, value: string) {
    setValues((v) => ({ ...v, [name]: value }));
    if (name in errors) {
      setErrors((e) => {
        const next = { ...e };
        delete next[name as FieldName];
        return next;
      });
    }
  }

  /** Translate a zod issue for a field into a localized message. */
  function messageForIssue(field: FieldName, code: string, validation?: string): string {
    // Email "invalid" surfaces as an "invalid_string" issue.
    if (field === "email" && (code === "invalid_string" || validation === "email")) {
      return t("validation.email.invalid");
    }
    if (code === "too_small") return t(`validation.${field}.required`, t("validation.generic"));
    if (code === "too_big") return t(`validation.${field}.max`, t("validation.generic"));
    return t("validation.generic");
  }

  function buildRequest(): ContactRequest {
    // Optional fields: send only when non-empty (schema treats them as optional).
    const trimmed = {
      name: values.name.trim(),
      email: values.email.trim(),
      message: values.message.trim(),
    };
    const req: ContactRequest = {
      name: trimmed.name,
      email: trimmed.email,
      message: trimmed.message,
      locale,
      website: values.website,
      renderedAt: renderedAtRef.current,
      ...(values.company.trim() ? { company: values.company.trim() } : {}),
      ...(values.phone.trim() ? { phone: values.phone.trim() } : {}),
      ...(values.subject.trim() ? { subject: values.subject.trim() } : {}),
      ...(turnstileToken ? { turnstileToken } : {}),
    };
    return req;
  }

  function mapErrorCode(code: ContactErrorCode | undefined): string {
    switch (code) {
      case "VALIDATION":
        return t("status.errors.validation");
      case "CSRF":
        return t("status.errors.csrf");
      case "ANTISPAM":
        return t("status.errors.antispam");
      case "DAILY_LIMIT":
        return t("status.errors.dailyLimit");
      case "UNAVAILABLE":
        return t("status.errors.unavailable");
      case "PAYLOAD_TOO_LARGE":
      case "LENGTH_REQUIRED":
      case "INTERNAL":
      default:
        return t("status.errors.internal");
    }
  }

  /** Apply server-returned field errors (details: { field: code }) when present. */
  function applyServerFieldErrors(details: Record<string, unknown> | undefined) {
    if (!details) return;
    const next: FieldErrors = {};
    for (const key of Object.keys(details)) {
      if (["name", "company", "email", "phone", "subject", "message"].includes(key)) {
        next[key as FieldName] = t("validation.generic");
      }
    }
    if (Object.keys(next).length) setErrors(next);
  }

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (pending) return;
    setFormError(null);

    const candidate = buildRequest();
    const parsed = contactRequestSchema.safeParse(candidate);

    if (!parsed.success) {
      const fieldErrors: FieldErrors = {};
      for (const issue of parsed.error.issues) {
        const field = issue.path[0];
        if (typeof field === "string" && field in EMPTY && field !== "website") {
          const f = field as FieldName;
          if (!fieldErrors[f]) {
            const validation =
              "validation" in issue ? (issue as { validation?: string }).validation : undefined;
            fieldErrors[f] = messageForIssue(f, issue.code, validation);
          }
        }
      }
      setErrors(fieldErrors);
      setFormError(t("status.errors.validation"));
      return;
    }

    setPending(true);
    try {
      const { status, data, networkError } = await submitContact(parsed.data);

      if (networkError) {
        setFormError(t("status.errors.network"));
        return;
      }

      if (status === 201) {
        setSucceeded(true);
        return;
      }

      const errEnvelope = (data as ContactResponse) as { error?: { code?: ContactErrorCode; details?: Record<string, unknown> } };
      const code = errEnvelope.error?.code;
      if (status === 400 || code === "VALIDATION") {
        applyServerFieldErrors(errEnvelope.error?.details);
      }
      setFormError(mapErrorCode(code));
    } finally {
      setPending(false);
    }
  }

  function resetForm() {
    setValues(EMPTY);
    setErrors({});
    setFormError(null);
    setTurnstileToken(null);
    setSucceeded(false);
  }

  const counter = useMemo(
    () => t("form.messageCounter", { count: values.message.length, max: MESSAGE_MAX }),
    [t, values.message.length],
  );

  if (succeeded) {
    return (
      <div
        className="rounded-[var(--radius-lg)] border border-[var(--brand-tint)] bg-[var(--brand-tint-3)]/50 p-8 text-center"
        role="status"
        aria-live="polite"
      >
        <span
          className="mx-auto mb-5 inline-flex h-14 w-14 items-center justify-center rounded-full bg-[var(--brand-primary)] text-white"
          aria-hidden="true"
        >
          <svg viewBox="0 0 24 24" className="h-7 w-7" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="m5 13 4 4L19 7" />
          </svg>
        </span>
        <h3 className="font-zh text-[var(--text-2xl)] font-bold text-[var(--ink)]">
          {t("status.success.title")}
        </h3>
        <p className="mx-auto mt-3 max-w-md text-[var(--text-base)] leading-[var(--leading-body)] text-[var(--ink)]/80">
          {t("status.success.body")}
        </p>
        <button
          type="button"
          className="mt-6 inline-flex items-center rounded-[var(--radius-md)] border border-[var(--brand-tint-2)] bg-white px-6 py-2.5 font-medium text-[var(--brand-azure-text)] transition-colors hover:border-[var(--brand-azure)] hover:bg-[var(--brand-tint-3)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-azure)]"
          onClick={resetForm}
        >
          {t("status.success.again")}
        </button>
      </div>
    );
  }

  return (
    <form
      className="relative rounded-[var(--radius-lg)] border border-[var(--brand-tint)] bg-white p-6 shadow-[var(--shadow-card)] md:p-8"
      onSubmit={handleSubmit}
      noValidate
    >
      <h2 className="font-zh text-[var(--text-2xl)] font-bold text-[var(--ink)]">
        {t("form.heading")}
      </h2>
      <p className="mt-2 text-[var(--text-base)] text-[var(--brand-slate-text)]">
        {t("form.subheading")}
      </p>

      {formError && (
        <p
          className="mt-5 rounded-[var(--radius-md)] border border-red-200 bg-red-50 px-4 py-3 text-[var(--text-sm)] font-medium text-red-700"
          role="alert"
        >
          {formError}
        </p>
      )}

      <div className="mt-6 grid gap-5 sm:grid-cols-2">
        <Field
          id="cf-name"
          name="name"
          type="text"
          required
          value={values.name}
          error={errors.name}
          autoComplete="name"
          onChange={(v) => setField("name", v)}
          label={t("form.fields.name.label")}
          placeholder={t("form.fields.name.placeholder")}
          requiredLabel={t("form.required")}
          optionalLabel={t("form.optional")}
        />

        <Field
          id="cf-company"
          name="company"
          type="text"
          value={values.company}
          error={errors.company}
          autoComplete="organization"
          onChange={(v) => setField("company", v)}
          label={t("form.fields.company.label")}
          placeholder={t("form.fields.company.placeholder")}
          requiredLabel={t("form.required")}
          optionalLabel={t("form.optional")}
        />

        <Field
          id="cf-email"
          name="email"
          type="email"
          required
          value={values.email}
          error={errors.email}
          autoComplete="email"
          inputMode="email"
          onChange={(v) => setField("email", v)}
          label={t("form.fields.email.label")}
          placeholder={t("form.fields.email.placeholder")}
          requiredLabel={t("form.required")}
          optionalLabel={t("form.optional")}
        />

        <Field
          id="cf-phone"
          name="phone"
          type="tel"
          value={values.phone}
          error={errors.phone}
          autoComplete="tel"
          inputMode="tel"
          onChange={(v) => setField("phone", v)}
          label={t("form.fields.phone.label")}
          placeholder={t("form.fields.phone.placeholder")}
          requiredLabel={t("form.required")}
          optionalLabel={t("form.optional")}
        />

        <div className="sm:col-span-2">
          <Field
            id="cf-subject"
            name="subject"
            type="text"
            value={values.subject}
            error={errors.subject}
            onChange={(v) => setField("subject", v)}
            label={t("form.fields.subject.label")}
            placeholder={t("form.fields.subject.placeholder")}
            requiredLabel={t("form.required")}
            optionalLabel={t("form.optional")}
          />
        </div>

        <div className="sm:col-span-2">
          <label htmlFor="cf-message" className={LABEL_CLASS}>
            {t("form.fields.message.label")}
            <span className={REQ_CLASS}>{t("form.required")}</span>
          </label>
          <textarea
            id="cf-message"
            name="message"
            className={`${inputClass(!!errors.message)} min-h-[9rem] resize-y`}
            value={values.message}
            required
            rows={6}
            maxLength={MESSAGE_MAX}
            aria-invalid={errors.message ? true : undefined}
            aria-describedby={errors.message ? "cf-message-error cf-message-counter" : "cf-message-counter"}
            placeholder={t("form.fields.message.placeholder")}
            onChange={(e) => setField("message", e.target.value)}
          />
          <div className="mt-1.5 flex items-start justify-between gap-3">
            {errors.message ? (
              <span id="cf-message-error" className={ERROR_CLASS} role="alert">
                {errors.message}
              </span>
            ) : (
              <span />
            )}
            <span id="cf-message-counter" className="shrink-0 text-[var(--text-xs)] text-[var(--brand-slate-text)]">
              {counter}
            </span>
          </div>
        </div>
      </div>

      {/* Honeypot: visually hidden, aria-hidden, removed from tab order. Bots fill it. */}
      <div
        className="absolute -left-[9999px] h-px w-px overflow-hidden"
        aria-hidden="true"
      >
        <label htmlFor="cf-website">Website</label>
        <input
          id="cf-website"
          name="website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={values.website}
          onChange={(e) => setField("website", e.target.value)}
        />
      </div>

      <Turnstile
        siteKey={turnstileSiteKey}
        locale={locale}
        onToken={setTurnstileToken}
        className="mt-6"
      />

      <button
        type="submit"
        className="mt-6 inline-flex w-full items-center justify-center rounded-[var(--radius-md)] bg-[var(--brand-primary)] px-7 py-3.5 text-[var(--text-base)] font-medium text-white shadow-[var(--shadow-card)] transition-colors hover:bg-[var(--brand-azure)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-azure)] disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
        disabled={pending}
      >
        {pending && (
          <svg
            className="-ml-1 mr-2.5 h-4 w-4 animate-spin"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.4 0 0 5.4 0 12h4z" />
          </svg>
        )}
        {pending ? t("form.submitting") : t("form.submit")}
      </button>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Field — a single labelled text input with error + required/optional marker.
// ---------------------------------------------------------------------------
interface FieldProps {
  id: string;
  name: string;
  type: "text" | "email" | "tel";
  value: string;
  label: string;
  placeholder: string;
  requiredLabel: string;
  optionalLabel: string;
  required?: boolean;
  error?: string;
  autoComplete?: string;
  inputMode?: "email" | "tel" | "text";
  onChange: (value: string) => void;
}

function Field({
  id,
  name,
  type,
  value,
  label,
  placeholder,
  requiredLabel,
  optionalLabel,
  required = false,
  error,
  autoComplete,
  inputMode,
  onChange,
}: FieldProps) {
  const errorId = `${id}-error`;
  return (
    <div>
      <label htmlFor={id} className={LABEL_CLASS}>
        {label}
        <span className={required ? REQ_CLASS : OPT_CLASS}>
          {required ? requiredLabel : optionalLabel}
        </span>
      </label>
      <input
        id={id}
        name={name}
        type={type}
        className={inputClass(!!error)}
        value={value}
        required={required}
        autoComplete={autoComplete}
        inputMode={inputMode}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
      {error && (
        <span id={errorId} className={`mt-1.5 block ${ERROR_CLASS}`} role="alert">
          {error}
        </span>
      )}
    </div>
  );
}
